import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import DashboardLayout from '../layout/DashboardLayout';
import { fetchProjects } from '../../store/slices/projectsSlice';
import Spinner from '../common/Spinner';
import DashboardHeader from '../dashboard/DashboardHeader';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock, FaCloud, FaThermometerHalf, FaWind, FaExchangeAlt, FaSearch, FaChevronLeft, FaChevronRight, FaEye, FaExternalLinkAlt, FaTint, FaSun, FaMoon } from 'react-icons/fa';
import { format } from 'date-fns';
import { config } from '../../config/config';
import { useThemeColors } from '../../hooks/useThemeColors';

// Set mapbox token
mapboxgl.accessToken = config.mapboxAccessToken;

const convertTemp = (fahrenheit, toCelsius) => {
  if (toCelsius) {
    return ((fahrenheit - 32) * 5/9).toFixed(0);
  }
  return Math.round(fahrenheit);
};

const WeatherAndMap = () => {
  const dispatch = useDispatch();
  const { buttonColors } = useThemeColors();
  const [searchTerm, setSearchTerm] = useState('');
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [mapProjects, setMapProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [geocodingErrors, setGeocodingErrors] = useState({});
  const [geocodingLoading, setGeocodingLoading] = useState(false);
  const { projects, loading } = useSelector(state => state.projects);
  const [projectWeatherData, setProjectWeatherData] = useState({});
  const [isCelsius, setIsCelsius] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isMobileListExpanded, setIsMobileListExpanded] = useState(false);
  const [weatherLoading, setWeatherLoading] = useState({});
  const [showUnmapped, setShowUnmapped] = useState(false);
  const [unmappedProjects, setUnmappedProjects] = useState([]);
  
  // Map refs
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markersRef = useRef({});
  const popupRef = useRef(null);
  const [mapStyle, setMapStyle] = useState('mapbox://styles/mapbox/streets-v11');
  const [mapReady, setMapReady] = useState(false);

  // Fetch weather for a single project
  const fetchWeatherForProject = useCallback(async (project) => {
    if (!project.coordinates?.length || projectWeatherData[project._id]) return;
    
    setWeatherLoading(prev => ({ ...prev, [project._id]: true }));
    try {
      const response = await fetch(
        `https://api.openweathermap.org/data/2.5/weather?lat=${project.coordinates[0]}&lon=${project.coordinates[1]}&appid=2beca0d64ac2991d51a88db54b92f8ca&units=imperial`
      );
      const data = await response.json();
      setProjectWeatherData(prev => ({ ...prev, [project._id]: data }));
    } catch (error) {
      console.error('Error fetching weather for project:', project._id, error);
    } finally {
      setWeatherLoading(prev => ({ ...prev, [project._id]: false }));
    }
  }, [projectWeatherData]);

  // Load all projects (no status filter)
  useEffect(() => {
    const loadProjects = async () => {
      try {
        await dispatch(fetchProjects({
          page: 1,
          limit: 1000 // Load all projects without status filter
        })).unwrap();
      } catch (error) {
        console.error('Error loading projects:', error);
      }
    };
    loadProjects();
  }, [dispatch]);

  // Client-side search filtering
  useEffect(() => {
    if (!mapProjects.length) return;
    
    if (!clientSearchTerm.trim()) {
      setFilteredProjects(mapProjects);
      return;
    }
    
    const searchLower = clientSearchTerm.toLowerCase();
    const filtered = mapProjects.filter(project => {
      const nameMatch = (project.client?.name || '').toLowerCase().includes(searchLower) ||
                       (project.siteContact?.name || '').toLowerCase().includes(searchLower);
      const addressMatch = (project.formattedAddress || '').toLowerCase().includes(searchLower) ||
                        (project.address?.street || '').toLowerCase().includes(searchLower) ||
                        (project.address?.city || '').toLowerCase().includes(searchLower);
      const jobNumberMatch = (project.jobNumber || '').toLowerCase().includes(searchLower);
      
      return nameMatch || addressMatch || jobNumberMatch;
    });
    
    setFilteredProjects(filtered);
    setCurrentPage(1);
  }, [mapProjects, clientSearchTerm]);

  // Format address helper - handles both structured and unstructured addresses
  const formatAddress = (project) => {
    const address = project.siteContact?.address || project.address || project.client?.address;
    if (!address) return null;

    const formatString = (str) => str?.trim().replace(/\s+/g, ' ');
    const street = formatString(address.street);
    const city = formatString(address.city);
    const state = formatString(address.state);
    const zipCode = address.zipCode?.trim();

    // If we have all structured fields, use them
    if (street && city && state) {
      let formattedAddress = `${street}, ${city}, ${state}`;
      if (zipCode) formattedAddress += ` ${zipCode}`;
      return formattedAddress;
    }

    // If street contains the full address (city/state missing), use street as-is
    // Check if street looks like a full address (contains comma or state abbreviation)
    if (street && (!city || !state)) {
      // Check if street already contains city/state pattern
      const hasFullAddress = street.includes(',') || 
        /\b(CA|AZ|NV|OR|WA|TX|FL|NY|CO|UT)\b/i.test(street);
      
      if (hasFullAddress) {
        return street;
      }
    }

    // If only street exists without full address pattern, can't geocode
    if (!street) return null;
    
    // Return whatever we have
    if (street && !city && !state) return null;
    
    let formattedAddress = street;
    if (city) formattedAddress += `, ${city}`;
    if (state) formattedAddress += `, ${state}`;
    if (zipCode) formattedAddress += ` ${zipCode}`;
    return formattedAddress;
  };

  // Geocode projects
  useEffect(() => {
    const getCoordinates = async () => {
      if (!projects?.length) {
        setMapProjects([]);
        setFilteredProjects([]);
        return;
      }

      setGeocodingLoading(true);
      const newGeocodingErrors = {};
      
      try {
        const projectsWithCoordinates = await Promise.all(
          projects.map(async (project) => {
            try {
              const formattedAddress = formatAddress(project);
              if (!formattedAddress) {
                newGeocodingErrors[project._id] = 'Invalid address';
                return project;
              }

              // Clean up the address for geocoding
              let searchAddress = formattedAddress;
              // Add USA if not already present
              if (!searchAddress.toLowerCase().includes('usa') && !searchAddress.toLowerCase().includes('united states')) {
                searchAddress = `${formattedAddress}, USA`;
              }
              
              const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(searchAddress)}.json?` + 
                new URLSearchParams({
                  access_token: config.mapboxAccessToken,
                  limit: 1,
                  types: 'address,place', // Allow both address and place types for flexibility
                  country: 'US',
                  autocomplete: true // Enable fuzzy matching
                });
              
              const response = await fetch(url);
              if (!response.ok) throw new Error('Geocoding failed');

              const data = await response.json();
              if (data?.features?.[0]) {
                const [lon, lat] = data.features[0].center;
                return {
                  ...project,
                  coordinates: [lat, lon],
                  formattedAddress: data.features[0].place_name,
                  geocodingConfidence: data.features[0].relevance
                };
              }
              
              newGeocodingErrors[project._id] = 'Address not found';
              return project;
            } catch (error) {
              newGeocodingErrors[project._id] = 'Geocoding error';
              return project;
            }
          })
        );

        setGeocodingErrors(newGeocodingErrors);
        const validProjects = projectsWithCoordinates.filter(p => p.coordinates?.length);
        const invalidProjects = projectsWithCoordinates.filter(p => !p.coordinates?.length);
        setMapProjects(validProjects);
        setFilteredProjects(validProjects);
        setUnmappedProjects(invalidProjects);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setGeocodingLoading(false);
      }
    };

    getCoordinates();
  }, [projects]);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapStyle,
      center: [-98.5795, 39.8283],
      zoom: 4,
      maxZoom: 18
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-left');
    map.current.addControl(new mapboxgl.FullscreenControl(), 'top-left');

    map.current.on('load', () => {
      setMapReady(true);
    });

    return () => {
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Update map style
  useEffect(() => {
    if (map.current && mapReady) {
      map.current.setStyle(mapStyle);
    }
  }, [mapStyle, mapReady]);

  // Add/update markers when projects change
  useEffect(() => {
    if (!map.current || !mapReady || !filteredProjects.length) return;

    // Clear existing markers
    Object.values(markersRef.current).forEach(marker => marker.remove());
    markersRef.current = {};

    const bounds = new mapboxgl.LngLatBounds();

    filteredProjects.forEach(project => {
      if (!project.coordinates?.length) return;

      const el = document.createElement('div');
      el.className = 'project-marker';
      el.innerHTML = `
        <div style="
          width: 32px; 
          height: 32px; 
          background: ${project.projectStatus === 'active' ? '#22c55e' : '#6b7280'};
          border-radius: 50%;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.2s;
        ">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
            <path d="M12 2L2 9.5V22h20V9.5L12 2zm0 2.5l7 5.5v10H5v-10l7-5.5z"/>
          </svg>
        </div>
      `;

      el.addEventListener('mouseenter', () => {
        const markerDiv = el.querySelector('div');
        if (markerDiv) markerDiv.style.transform = 'scale(1.2)';
      });
      el.addEventListener('mouseleave', () => {
        const markerDiv = el.querySelector('div');
        if (markerDiv && selectedProject?._id !== project._id) {
          markerDiv.style.transform = 'scale(1)';
        }
      });

      const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
        .setLngLat([project.coordinates[1], project.coordinates[0]])
        .addTo(map.current);

      el.addEventListener('click', () => handleProjectSelect(project));

      markersRef.current[project._id] = marker;
      bounds.extend([project.coordinates[1], project.coordinates[0]]);
    });

    if (!bounds.isEmpty() && !selectedProject) {
      map.current.fitBounds(bounds, { padding: 60, maxZoom: 12, duration: 1000 });
    }
  }, [filteredProjects, mapReady]);

  // Create popup HTML content
  const createPopupContent = useCallback((project, weather) => {
    const displayAddress = project.formattedAddress || formatAddress(project) || '';
    const weatherHtml = weather ? `
      <div style="display: flex; align-items: center; gap: 8px; margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb;">
        <img src="https://openweathermap.org/img/wn/${weather.weather[0].icon}@2x.png" alt="weather" style="width: 48px; height: 48px;" />
        <div>
          <div style="font-size: 20px; font-weight: bold; color: #1f2937;">
            ${convertTemp(weather.main.temp, isCelsius)}°${isCelsius ? 'C' : 'F'}
          </div>
          <div style="font-size: 12px; color: #6b7280; text-transform: capitalize;">${weather.weather[0].description}</div>
          <div style="font-size: 11px; color: #9ca3af; margin-top: 2px;">
            Feels like ${convertTemp(weather.main.feels_like, isCelsius)}°${isCelsius ? 'C' : 'F'} • 💨 ${Math.round(weather.wind.speed)} mph • 💧 ${weather.main.humidity}%
          </div>
        </div>
      </div>
    ` : `<div style="font-size: 12px; color: #9ca3af; margin-top: 8px;">Loading weather...</div>`;

    return `
      <div style="font-family: system-ui, -apple-system, sans-serif; min-width: 220px;">
        <div style="font-weight: 600; font-size: 14px; color: #1f2937;">${project.jobNumber}</div>
        <div style="font-size: 13px; color: #4b5563; margin-top: 2px;">${project.client?.name || ''}</div>
        <div style="font-size: 12px; color: #6b7280; margin-top: 4px;">${displayAddress}</div>
        ${weatherHtml}
      </div>
    `;
  }, [isCelsius]);

  // Show popup at location
  const showPopupAtLocation = useCallback((project, weather) => {
    if (!map.current || !project.coordinates?.length) return;

    // Remove existing popup
    if (popupRef.current) {
      popupRef.current.remove();
    }

    // Create new popup
    popupRef.current = new mapboxgl.Popup({
      offset: [0, -20],
      closeButton: true,
      closeOnClick: false,
      maxWidth: '320px'
    })
      .setLngLat([project.coordinates[1], project.coordinates[0]])
      .setHTML(createPopupContent(project, weather))
      .addTo(map.current);
  }, [createPopupContent]);

  // Handle project selection and zoom
  const handleProjectSelect = useCallback((project) => {
    setSelectedProject(project);
    fetchWeatherForProject(project);
    
    if (project.coordinates?.length && map.current) {
      // Close mobile list
      setIsMobileListExpanded(false);
      
      // Fly to location with high zoom
      map.current.flyTo({
        center: [project.coordinates[1], project.coordinates[0]],
        zoom: 17,
        pitch: 45,
        bearing: 0,
        duration: 1500,
        essential: true
      });

      // Show popup at marker location
      const weather = projectWeatherData[project._id];
      showPopupAtLocation(project, weather);

      // Update marker styles with proper null checks
      Object.entries(markersRef.current).forEach(([id, marker]) => {
        try {
          const el = marker?.getElement?.();
          const markerDiv = el?.querySelector('div');
          if (markerDiv && markerDiv.style) {
            if (id === project._id) {
              markerDiv.style.transform = 'scale(1.3)';
              markerDiv.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.5), 0 4px 12px rgba(0,0,0,0.4)';
            } else {
              markerDiv.style.transform = 'scale(1)';
              markerDiv.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
            }
          }
        } catch (err) {
          // Silently ignore marker style errors
        }
      });
    }
  }, [fetchWeatherForProject, projectWeatherData, showPopupAtLocation]);

  // Update popup when weather data loads
  useEffect(() => {
    if (selectedProject && projectWeatherData[selectedProject._id] && popupRef.current) {
      popupRef.current.setHTML(createPopupContent(selectedProject, projectWeatherData[selectedProject._id]));
    }
  }, [selectedProject, projectWeatherData, createPopupContent]);

  // Zoom to project location
  const handleZoomToLocation = useCallback((project) => {
    if (!project.coordinates?.length || !map.current) {
      console.warn('Cannot zoom: no coordinates or map not ready');
      return;
    }
    
    handleProjectSelect(project);
  }, [handleProjectSelect]);

  // Get paginated projects
  const paginatedProjects = useMemo(() => {
    const startIndex = (currentPage - 1) * limit;
    return filteredProjects.slice(startIndex, startIndex + limit);
  }, [filteredProjects, currentPage, limit]);

  const totalPages = useMemo(() => Math.ceil(filteredProjects.length / limit), [filteredProjects.length, limit]);

  const getGoogleMapsUrl = (address) => {
    if (!address) return null;
    const query = encodeURIComponent(`${address.street}, ${address.city}, ${address.state} ${address.zipCode || ''}`);
    return `https://www.google.com/maps/search/?api=1&query=${query}`;
  };

  // Map style options
  const mapStyles = [
    { id: 'mapbox://styles/mapbox/streets-v11', name: 'Streets', icon: '🗺️' },
    { id: 'mapbox://styles/mapbox/satellite-streets-v11', name: 'Satellite', icon: '🛰️' },
    { id: 'mapbox://styles/mapbox/light-v10', name: 'Light', icon: '☀️' },
    { id: 'mapbox://styles/mapbox/dark-v10', name: 'Dark', icon: '🌙' },
  ];

  // Compact Project Card Component
  const ProjectCard = ({ project, isSelected }) => {
    const weather = projectWeatherData[project._id];
    const isLoadingWeather = weatherLoading[project._id];
    const displayAddress = project.siteContact?.address || project.address || project.client?.address;

    return (
      <div 
        className={`
          bg-white rounded-xl border-2 transition-all duration-200 overflow-hidden
          ${isSelected 
            ? 'border-blue-500 shadow-lg ring-2 ring-blue-100' 
            : 'border-gray-100 hover:border-gray-200 hover:shadow-md'
          }
        `}
      >
        {/* Header */}
        <div 
          className={`px-4 py-3 cursor-pointer ${isSelected ? 'bg-blue-50' : 'bg-gray-50'}`}
          onClick={() => handleProjectSelect(project)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-2.5 h-2.5 rounded-full ${
                project.projectStatus === 'active' ? 'bg-green-500' : 'bg-gray-400'
              }`} />
              <span className="font-semibold text-gray-900">{project.jobNumber}</span>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              project.projectStatus === 'active' 
                ? 'bg-green-100 text-green-700' 
                : 'bg-gray-100 text-gray-600'
            }`}>
              {project.projectStatus}
            </span>
          </div>
          <p className="text-sm text-gray-600 mt-1 font-medium truncate">
            {project.client?.name || project.siteContact?.name || 'No client name'}
          </p>
        </div>

        {/* Body */}
        <div className="px-4 py-3 space-y-3">
          {/* Address */}
          {displayAddress && (
            <div className="text-sm text-gray-600">
              <p className="truncate">{displayAddress.street}</p>
              <p className="text-gray-500">{displayAddress.city}, {displayAddress.state}</p>
            </div>
          )}

          {/* Weather */}
          {weather && (
            <div className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-sky-50 rounded-lg p-2.5">
              <div className="flex items-center gap-2">
                <img 
                  src={`https://openweathermap.org/img/wn/${weather.weather[0].icon}.png`}
                  alt="weather"
                  className="w-10 h-10"
                />
                <div>
                  <p className="text-lg font-bold text-gray-800">
                    {convertTemp(weather.main.temp, isCelsius)}°{isCelsius ? 'C' : 'F'}
                  </p>
                  <p className="text-xs text-gray-500 capitalize">{weather.weather[0].description}</p>
                </div>
              </div>
              <div className="text-right text-xs text-gray-500 space-y-0.5">
                <div className="flex items-center gap-1 justify-end">
                  <FaTint className="w-3 h-3 text-blue-400" />
                  <span>{weather.main.humidity}%</span>
                </div>
                <div className="flex items-center gap-1 justify-end">
                  <FaWind className="w-3 h-3 text-gray-400" />
                  <span>{Math.round(weather.wind.speed)} mph</span>
                </div>
              </div>
            </div>
          )}

          {isLoadingWeather && (
            <div className="flex items-center justify-center py-2">
              <Spinner size="sm" />
              <span className="text-xs text-gray-500 ml-2">Loading weather...</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleZoomToLocation(project);
              }}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <FaEye className="w-3.5 h-3.5" />
              <span>View on Map</span>
            </button>
            {displayAddress && (
              <a
                href={getGoogleMapsUrl(displayAddress)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center justify-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors"
              >
                <FaExternalLinkAlt className="w-3 h-3" />
              </a>
            )}
          </div>

          {/* Contact Info */}
          {(project.client?.cellPhone || project.client?.email) && (
            <div className="flex items-center gap-3 text-xs text-gray-500 pt-1 border-t border-gray-100">
              {project.client?.cellPhone && (
                <div className="flex items-center gap-1">
                  <FaPhone className="w-3 h-3" />
                  <span>{project.client.cellPhone}</span>
                </div>
              )}
              {project.client?.email && (
                <div className="flex items-center gap-1 truncate">
                  <FaEnvelope className="w-3 h-3" />
                  <span className="truncate">{project.client.email}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Custom popup styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .mapboxgl-popup-content {
        padding: 12px 16px !important;
        border-radius: 12px !important;
        box-shadow: 0 4px 20px rgba(0,0,0,0.15) !important;
      }
      .mapboxgl-popup-close-button {
        font-size: 18px !important;
        padding: 4px 8px !important;
        color: #6b7280 !important;
      }
      .mapboxgl-popup-close-button:hover {
        color: #1f2937 !important;
        background: transparent !important;
      }
      .mapboxgl-popup-tip {
        border-top-color: white !important;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  return (
    <DashboardLayout>
      <DashboardHeader 
        hideCreateLead={true}
        hideActions={true}
        hideTabBar={true}
        hideSearch={true}
      />
      
      <div className="h-[calc(100vh-80px)] flex flex-col md:flex-row bg-gray-100">
        {/* Sidebar - Project List */}
        <div className={`
          ${isMobileListExpanded ? 'h-[70vh]' : 'h-auto'}
          md:h-full md:w-96 flex flex-col bg-white border-r border-gray-200
          fixed md:relative bottom-0 left-0 right-0 z-20 md:z-auto
          transition-all duration-300 rounded-t-2xl md:rounded-none shadow-lg md:shadow-none
        `}>
          {/* Mobile Handle */}
          <div 
            className="md:hidden flex items-center justify-center py-2 cursor-pointer border-b"
            onClick={() => setIsMobileListExpanded(!isMobileListExpanded)}
          >
            <div className="w-10 h-1 bg-gray-300 rounded-full" />
          </div>

          {/* Header */}
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Projects</h2>
                <p className="text-sm text-gray-500">
                  {filteredProjects.length} mapped of {projects?.length || 0} total
                </p>
              </div>
              <button
                onClick={() => setIsCelsius(!isCelsius)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
              >
                <FaExchangeAlt className="w-3 h-3" />
                <span>{isCelsius ? '°C' : '°F'}</span>
              </button>
            </div>
            
            {/* Toggle between mapped and unmapped */}
            {unmappedProjects.length > 0 && (
              <div className="flex gap-2 mb-3">
                <button
                  onClick={() => setShowUnmapped(false)}
                  className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                    !showUnmapped 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Mapped ({filteredProjects.length})
                </button>
                <button
                  onClick={() => setShowUnmapped(true)}
                  className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                    showUnmapped 
                      ? 'bg-orange-500 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Unmapped ({unmappedProjects.length})
                </button>
              </div>
            )}
            
            {/* Search */}
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name, address, job #..."
                value={clientSearchTerm}
                onChange={(e) => setClientSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Items per page & Pagination - at top */}
          {!showUnmapped && (
            <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between bg-gray-50">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className={`p-1.5 rounded transition-colors ${
                    currentPage === 1 
                      ? 'text-gray-300 cursor-not-allowed' 
                      : 'text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <FaChevronLeft className="w-3 h-3" />
                </button>
                <span className="text-sm text-gray-600 min-w-[60px] text-center">
                  {currentPage} / {totalPages || 1}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className={`p-1.5 rounded transition-colors ${
                    currentPage >= totalPages 
                      ? 'text-gray-300 cursor-not-allowed' 
                      : 'text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <FaChevronRight className="w-3 h-3" />
                </button>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500">Show:</span>
                <select 
                  value={limit} 
                  onChange={(e) => { setLimit(Number(e.target.value)); setCurrentPage(1); }}
                  className="text-sm border-0 bg-transparent focus:outline-none focus:ring-0 text-gray-700 font-medium cursor-pointer"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          )}

          {/* Project List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {loading || geocodingLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Spinner size="lg" />
                <p className="mt-3 text-sm text-gray-500">
                  {loading ? 'Loading projects...' : 'Mapping addresses...'}
                </p>
              </div>
            ) : showUnmapped ? (
              // Show unmapped projects
              unmappedProjects.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-3">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-500 font-medium">All projects mapped!</p>
                </div>
              ) : (
                unmappedProjects.map((project) => {
                  const address = project.siteContact?.address || project.address || project.client?.address;
                  const errorReason = geocodingErrors[project._id] || 'Unknown error';
                  
                  return (
                    <div 
                      key={project._id}
                      className="bg-white rounded-xl border-2 border-orange-200 overflow-hidden"
                    >
                      <div className="px-4 py-3 bg-orange-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />
                            <span className="font-semibold text-gray-900">{project.jobNumber}</span>
                          </div>
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-orange-100 text-orange-700">
                            {errorReason}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1 font-medium truncate">
                          {project.client?.name || project.siteContact?.name || 'No client name'}
                        </p>
                      </div>
                      <div className="px-4 py-3">
                        <div className="text-sm space-y-2">
                          <p className="font-medium text-gray-700">Address Data:</p>
                          {address ? (
                            <div className="bg-gray-50 rounded-lg p-2 text-xs font-mono space-y-1">
                              <p><span className="text-gray-500">street:</span> <span className={address.street ? 'text-gray-800' : 'text-red-400'}>{address.street || '(empty)'}</span></p>
                              <p><span className="text-gray-500">city:</span> <span className={address.city ? 'text-gray-800' : 'text-red-400'}>{address.city || '(empty)'}</span></p>
                              <p><span className="text-gray-500">state:</span> <span className={address.state ? 'text-gray-800' : 'text-red-400'}>{address.state || '(empty)'}</span></p>
                              <p><span className="text-gray-500">zip:</span> <span className={address.zipCode ? 'text-gray-800' : 'text-gray-400'}>{address.zipCode || '(empty)'}</span></p>
                            </div>
                          ) : (
                            <p className="text-red-500 italic">No address on file</p>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-3">
                          💡 Make sure city and state are in separate fields, not all in street
                        </p>
                      </div>
                    </div>
                  );
                })
              )
            ) : paginatedProjects.length === 0 ? (
              <div className="text-center py-12">
                <FaMapMarkerAlt className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500 font-medium">No projects found</p>
                <p className="text-sm text-gray-400 mt-1">Try a different search term</p>
              </div>
            ) : (
              paginatedProjects.map((project) => (
                <ProjectCard
                  key={project._id}
                  project={project}
                  isSelected={selectedProject?._id === project._id}
                />
              ))
            )}
          </div>

        </div>

        {/* Map Container */}
        <div className="flex-1 relative h-[60vh] md:h-full">
          {/* Map Style Selector */}
          <div className="absolute top-4 right-4 z-10 bg-white rounded-xl shadow-lg p-2">
            <div className="flex gap-1">
              {mapStyles.map(style => (
                <button
                  key={style.id}
                  onClick={() => setMapStyle(style.id)}
                  title={style.name}
                  className={`w-9 h-9 rounded-lg flex items-center justify-center text-lg transition-all ${
                    mapStyle === style.id 
                      ? 'bg-blue-500 shadow-md' 
                      : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  {style.icon}
                </button>
              ))}
            </div>
          </div>


          {/* Map */}
          <div ref={mapContainer} className="w-full h-full" />

          {/* Mobile: Tap to expand hint */}
          {!isMobileListExpanded && (
            <div 
              className="md:hidden absolute bottom-20 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur px-4 py-2 rounded-full shadow-lg cursor-pointer"
              onClick={() => setIsMobileListExpanded(true)}
            >
              <p className="text-sm text-gray-600 font-medium">↑ Swipe up for project list</p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default WeatherAndMap;
